import React from 'react';
import {
    FormControl,
    FormHeader,
    FormLabel,
    FormElement,
    FormTooltip,
    FormHelperText,
    InputSuffix,
    InputGroup,
    Input,
    useFormState,
} from '@fiverr-private/forms';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { getInputStateIcon } from '../../utils/getInputStateIcon';

export interface FormInputFieldProps {
    name: string;
    label: string;
    tooltip?: string;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    isValidationFieldSuccess?: boolean;
    flex?: React.ComponentProps<typeof Container>['flex'];
    onChange?(value: string): void;
}

const Wrapper: React.FC<Pick<FormInputFieldProps, 'flex'>> = ({ flex, children }) =>
    typeof flex === 'undefined' ? (
        <React.Fragment>{children}</React.Fragment>
    ) : (
        <Container flex={flex}>{children}</Container>
    );

export const FormInputField: React.FC<FormInputFieldProps> = ({
    flex,
    name,
    label,
    placeholder,
    tooltip,
    isValidationFieldSuccess,
    autoFocus,
    disabled,
    onChange = () => null,
}) => {
    const { isValidating, errors } = useFormState({ name });

    const id = `id-${name}`;
    const icon = getInputStateIcon({
        loading: isValidating,
        error: Boolean(errors[name]),
        success: isValidationFieldSuccess,
    });

    return (
        <Wrapper flex={flex}>
            <FormControl name={name}>
                <FormHeader>
                    <FormLabel htmlFor={id}>
                        <Typography fontWeight="semibold">{label}</Typography>
                    </FormLabel>
                    {tooltip && <FormTooltip content={tooltip} />}
                </FormHeader>
                <FormElement>
                    <InputGroup disabled={disabled} onChange={(e) => onChange(e.target.value)}>
                        <Input id={id} placeholder={placeholder} autoFocus={autoFocus} />
                        <InputSuffix>{icon}</InputSuffix>
                    </InputGroup>
                </FormElement>
                <FormHelperText />
            </FormControl>
        </Wrapper>
    );
};
